/* eslint-disable @typescript-eslint/no-use-before-define */
import { QueryStatus } from '@reduxjs/toolkit/query'

import useStateNonblocking from '@Hooks/useStateNonblocking'
import type { PaginatedResponse } from '@Store/types'
import type { Product } from '@Store/types/product'

import ProductsEmpty from './ProductsEmpty'
import ProductsErrorResult from './ProductsErrorResult'
import ProductsNoResult from './ProductsNoResult'
import ProductRow from './row'

interface ProductsListProps {
  products?: PaginatedResponse<Product>
  isInFreezePeriod?: boolean
}

function ProductListWithLoading({
  isInitiated = false,
  isLoading = false,
  fetchStatus,
  fetcher = (preferCache?: boolean): void => {},
  hasFilters = false,
  ...props
}: {
  isInitiated?: boolean
  isLoading: boolean
  fetchStatus?: QueryStatus
  fetcher?: (preferCache?: boolean) => void
  hasFilters?: boolean
} & ProductsListProps): JSX.Element {
  if (!isInitiated) {
    return <ProductListLoading />
  }

  if (isLoading) {
    return <ProductRowLoading />
  }

  if (fetchStatus === QueryStatus.rejected) {
    return <ProductsErrorResult fetcher={fetcher} />
  }

  if (
    fetchStatus === QueryStatus.fulfilled &&
    (!props?.products ||
      !props.products.data.length ||
      !props.products.meta?.totalItems)
  ) {
    return hasFilters ? <ProductsNoResult /> : <ProductsEmpty />
  }

  return <ProductsList {...props} />
}

export default ProductListWithLoading

function ProductsList({
  products,
  isInFreezePeriod = false,
}: ProductsListProps): JSX.Element {
  const [ProductList, isProductListPending] = useStateNonblocking<
    JSX.Element[]
  >(
    () => {
      if (!products || !products.data.length) {
        return []
      }

      return products.data.map((value) => (
        <ProductRow
          product={value}
          key={value.id}
          isInFreezePeriod={isInFreezePeriod}
        />
      ))
    },
    [products],
    [],
  )

  // No Products case handled in ProductListWithLoading
  // this is to handle the case where the products are not rendered yet
  if (isProductListPending || !ProductList.length) {
    return <ProductRowLoading />
  }

  return <>{ProductList}</>
}

function ProductListLoading(): JSX.Element {
  return <ProductRowLoading />
}

const ProductRowLoading = (): JSX.Element => (
  <div className="w-full border border-solid border-gray-200 rounded-lg flex flex-col mt-6">
    <div className="flex flex-col pt-5 px-4 pb-16 space-y-4">
      <div className="flex flex-col space-y-1">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row space-x-2">
            <div className="animate-pulse bg-gray-200 h-5 w-20 rounded-md"></div>
            <div className="animate-pulse bg-gray-200 h-5 w-20 rounded-md"></div>
          </div>
          <div className="flex flex-row space-x-3">
            <div className="animate-pulse bg-gray-200 h-5 w-20 rounded-md"></div>
            <div className="animate-pulse bg-gray-200 h-5 w-5 rounded-md"></div>
          </div>
        </div>
        <div className="animate-pulse bg-gray-200 h-5 w-40 rounded-md"></div>
      </div>
      <div className="flex flex-col space-y-1">
        <div className="flex flex-row space-x-3.5">
          <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
        </div>
        <div className="flex flex-row space-x-3.5">
          <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-4 w-12 rounded"></div>
        </div>
      </div>
      <div className="flex flex-row space-x-2">
        <div className="animate-pulse bg-gray-200 h-5 w-16 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-5 w-16 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-5 w-16 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-5 w-16 rounded"></div>
      </div>
      <div className="flex flex-row justify-between">
        <div className="animate-pulse bg-gray-200 h-7 w-28 rounded"></div>
        <div className="flex flex-row space-x-1">
          <div className="animate-pulse bg-gray-200 h-7 w-20 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-7 w-20 rounded"></div>
        </div>
      </div>
    </div>
    <div className="animate-pulse w-full h-96 bg-gray-200 rounded"></div>
    <div className="pt-3 px-6 pb-4 flex flex-col space-y-3">
      <div className="animate-pulse bg-gray-200 h-4 w-72 rounded"></div>
      <div className="flex flex-row space-x-3">
        <div className="animate-pulse bg-gray-200 h-9 w-24 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-9 w-24 rounded"></div>
      </div>
    </div>
  </div>
)
